import React from 'react';
import { arrayOf, bool, oneOf, shape } from 'prop-types';
import { Avatar, Box, Paper, Typography } from '@mui/material';
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';

import { useTheme } from '@mui/material/styles';
import { PTO_STATUS } from '../../utils';
import OpenCard from '../card/CardConnector';
import CardSkeleton from '../card/CardSkeleton';
import { swimlaneColumnStyles } from './Swimlane.styles';

// Swimlane component - renders lists of OpenPtoCards based on PTO status. Renders Skeleton during initial data fetch
const Swimlane = ({ fetchPtoListPending, filteredPtoList, swimlaneType }) => {
  const theme = useTheme();
  const SWIMLANE_OPTIONS = {
    approvalneeded: {
      avatarColor: 'warning.light',
      emptyMessage: 'that need your approval.',
      headerText: 'Needs My Approval',
      iconColor: 'warning.dark',
    },
    approved: {
      avatarColor: 'error.light',
      emptyMessage: 'that cannot be booked.',
      headerText: 'Cannot Book',
      iconColor: 'error.main',
    },
    pendingapproval: {
      avatarColor: 'warning.light',
      emptyMessage: 'that are pending approval.',
      headerText: 'Needs Recipient Approval',
      iconColor: 'warning.dark',
    },
    rejected: {
      avatarColor: 'graphic.light',
      emptyMessage: 'that have been rejected.',
      headerText: 'Recipient Rejected',
      iconColor: 'graphic.main',
    },
    scheduled: {
      avatarColor: 'success.light',
      emptyMessage: 'that are scheduled.',
      headerText: 'Scheduled',
      iconColor: 'blue.main',
    },
  };

  const filteredListBySwimlane = filteredPtoList.filter(pto => pto.status === swimlaneType);

  return (
    <Box sx={{ ...swimlaneColumnStyles(theme) }}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: 'surface.swimlane',
          borderRadius: 0.5,
          boxShadow: 'none',
          height: 'auto',
          pb: 2,
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          width: { xs: 336, lg: '100%' },
        }}
      >
        <Box
          display="flex"
          sx={{
            pb: 0.5,
            pl: 2,
            pr: 0,
            pt: 2,
          }}
        >
          <Avatar
            sx={{
              '& svg': {
                height: '75%',
                width: '75%',
              },
              backgroundColor: SWIMLANE_OPTIONS[swimlaneType.toLowerCase()].avatarColor,
              height: 24,
              width: 24,
            }}
          >
            <RepeatRoundedIcon
              sx={{ color: SWIMLANE_OPTIONS[swimlaneType.toLowerCase()].iconColor }}
            />
          </Avatar>
          <Typography
            sx={{
              alignSelf: 'center',
              fontWeight: 700,
              pl: 1.5,
            }}
            variant="body1"
          >
            {SWIMLANE_OPTIONS[swimlaneType.toLowerCase()].headerText}
          </Typography>
        </Box>
        {fetchPtoListPending ? (
          <CardSkeleton />
        ) : (
          filteredPtoList
            .filter(pto => pto.status === swimlaneType)
            .map(pto => <OpenCard data={pto} key={pto.ptoNumber} />)
        )}
        {!fetchPtoListPending && filteredListBySwimlane.length < 1 && (
          <Box bgcolor="graphic.lighter" borderRadius={0.5} m={1} py={3} textAlign="center">
            <DoneAllRoundedIcon sx={{ color: 'graphic.main', fontSize: 50 }} />
            <Typography variant="body1"> There are no orders </Typography>
            <Typography variant="body1">
              {SWIMLANE_OPTIONS[swimlaneType.toLowerCase()].emptyMessage}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

Swimlane.propTypes = {
  fetchPtoListPending: bool.isRequired,
  filteredPtoList: arrayOf(shape({})),
  swimlaneType: oneOf([
    PTO_STATUS.approvalNeeded,
    PTO_STATUS.approved,
    PTO_STATUS.pendingApproval,
    PTO_STATUS.rejected,
  ]),
};

Swimlane.defaultProps = {
  filteredPtoList: null,
  swimlaneType: '',
};

export default Swimlane;
